import { FormControl, Grid, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import AsyncSearch from "../../components/asyncSearch/AsyncSearch";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICriteria } from "../../interfaces/ICriteria";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IJudgmentCriteria } from "../../interfaces/IJudgmentCriteria";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const JudgmentCriteria: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();
  const timeout = useRef<any>(0);

  const [judgmentCriteriaId, setJudgmentCriteriaId] = useState("");

  const [criteriaOptions, setCriteriaOptions] = useState<IOption[]>([]);
  const [criteriaOption, setCriteriaOption] = useState<IOption | null>(null);
  const [loadingCriteria, setLoadingCriteria] = useState(false);

  const [maximum, setMaximum] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const criteriaToOption = (criteria: ICriteria): IOption => {
    return {
      value: `${criteria.id}`,
      description: `${criteria.name}`,
    };
  };

  const handleSetCriteria = (option: IOption | null) => {
    if (option) {
      setCriteriaOption(option);
      const selected = criteriaOptions.find(criteriaOption => {
        return criteriaOption.value === option.value;
      });
      if (selected?.value) {
        setCriteriaOption(selected);
      } else {
        setCriteriaOption(null);
      }
    }
  };

  const handleListCriteria = useCallback(
    async (filter = "") => {
      setLoadingCriteria(true);

      const judgmentTypeId =
        new URLSearchParams(location.search).get("judgmentTypeId") || "";

      await api
        .get(
          `/criteria/dynamically/${judgmentTypeId}?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}`,
        )
        .then(result => {
          if (result) {
            const options: IOption[] = result.data.data.map(criteriaToOption);

            setCriteriaOptions(options);
          } else {
            setCriteriaOptions([]);
          }
        })
        .catch(error => {
          setCriteriaOptions([]);
          toastr.error(error.message);
        })
        .finally(() => {
          setLoadingCriteria(false);
        });
    },
    [toastr, location.search],
  );

  const handleListCriteriaAsync = async (filter = "") => {
    clearInterval(timeout.current);
    timeout.current = setTimeout(async () => {
      await handleListCriteria(filter);
    }, timeout.current);
  };

  const handleDeleteJudgmentCriteria = useCallback(
    async (judgmentCriteriaId: string) => {
      await api
        .delete(`/judgment-criteria/${judgmentCriteriaId}`)
        .then(() => {
          toastr.success("Critério de julgamento excluído com sucesso");
          handleGoBack();
        })
        .catch(error => {
          toastr.error(error?.message || "Contate a equipe de suporte");
        });
    },
    [handleGoBack, toastr],
  );

  const handleListJudgmentCriteria = useCallback(async () => {
    const id = location.pathname
      .replace("/judgment-criteria", "")
      .replace("/", "");

    await handleListCriteria();

    if (id) {
      setJudgmentCriteriaId(id);
      setLoading(true);

      await api
        .get(`/judgment-criteria/${id}`)
        .then(response => {
          const judgmentCriteria: IJudgmentCriteria = response.data;

          if (judgmentCriteria.criteria) {
            const criteriaOption = criteriaToOption(judgmentCriteria.criteria);

            criteriaOption && setCriteriaOption(criteriaOption);
          }

          setMaximum(
            judgmentCriteria.maximum
              ? judgmentCriteria.maximum.toString()
              : null,
          );
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname, handleListCriteria]);

  const handleSubmit = useCallback(async () => {
    const judgmentId =
      new URLSearchParams(location.search).get("judgmentId") || "";

    try {
      setFormErrors({});
      setLoading(true);

      const data: IJudgmentCriteria = {
        judgmentId,
        criteriaId: criteriaOption?.value || "",
        maximum: Number(maximum),
      };

      const schema = Yup.object().shape({
        criteriaId: Yup.string().required("Critério é obrigatório"),
        maximum: Yup.number()
          .typeError("Máximo deve ser um número")
          .moreThan(0, "Máximo deve ser maior que 0")
          .lessThan(100, "Máximo deve ser menor que 100")
          .required("Máximo é obrigatório"),
      });

      if (judgmentCriteriaId) {
        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/judgment-criteria/${judgmentCriteriaId}`, data)
          .then(async () => {
            toastr.success("Critério de julgamento atualizada com sucesso");
            handleGoBack();
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/judgment-criteria`, data)
          .then(() => {
            toastr.success("Critério de julgamento cadastrada com sucesso");
            handleGoBack();
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    judgmentCriteriaId,
    criteriaOption?.value,
    maximum,
    navigate,
    signOut,
    toastr,
    handleGoBack,
    location.search,
  ]);

  useEffect(() => {
    handleListJudgmentCriteria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Criterio de Julgamento</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                size="small"
                error={!!formErrors.criteriaId}
                sx={{ width: "100%" }}
                required
              >
                <AsyncSearch
                  options={criteriaOptions}
                  setOptions={setCriteriaOptions}
                  option={criteriaOption}
                  setOption={handleSetCriteria}
                  asyncSearch={handleListCriteriaAsync}
                  loading={loadingCriteria}
                  error={Boolean(formErrors?.criteriaId)}
                  errorMessage={formErrors?.criteriaId || null}
                  label="Critério"
                  required
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"99"}
                value={maximum || ""}
                onChange={e => setMaximum(e.target.value)}
                maskPlaceholder={null}
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Nota Máxima"
                  required
                  helperText={formErrors.maximum}
                  error={!!formErrors.maximum}
                />
              </InputMask>
            </Grid>
          </Grid>
          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={handleGoBack}
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {judgmentCriteriaId && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Excluir
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={judgmentCriteriaId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${judgmentCriteriaId}`}
          handleConfirmation={handleDeleteJudgmentCriteria}
          deny={() => setDialogDeleteOpen(false)}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
